import { meQuery } from "@auth/me.query"
import { createFileRoute, getRouteApi, notFound } from "@tanstack/react-router"
import type { ReactElement } from "react"

import { canViewErrors } from "./-enabled"
import { ErrorState } from "./-Error"
import {
  DEFAULT_MONTH_WINDOW,
  errorReports,
  isMonthWindow,
  type MonthWindow,
} from "./-errorReports.query"
import { Errors } from "./-Errors"
import { Loading } from "./-Loading"

type ErrorsSearch = {
  months?: MonthWindow
  solution?: string
  user?: string
  group?: string
}

const asText = (value: unknown): string | undefined =>
  typeof value === "string" && value !== "" ? value : undefined

const validateSearch = (search: Record<string, unknown>): ErrorsSearch => ({
  months: isMonthWindow(search.months) ? search.months : undefined,
  solution: asText(search.solution),
  user: asText(search.user),
  group: asText(search.group),
})

const route = getRouteApi("/_chrome/superpowers/errors/")

const RouteComponent = (): ReactElement => {
  const { months, solution, user, group } = route.useSearch()
  const navigate = route.useNavigate()

  return (
    <Errors
      months={months ?? DEFAULT_MONTH_WINDOW}
      solutionUid={solution}
      userUid={user}
      groupKey={group}
      onFilterChange={(next) => {
        void navigate({
          search: (current) => ({
            ...current,
            ...("months" in next ? { months: next.months } : {}),
            ...("solutionUid" in next ? { solution: next.solutionUid } : {}),
            ...("userUid" in next ? { user: next.userUid } : {}),
          }),
        })
      }}
      onSelectGroup={(key) => {
        void navigate({ search: (current) => ({ ...current, group: key }) })
      }}
    />
  )
}

export const Route = createFileRoute("/_chrome/superpowers/errors/")({
  validateSearch,
  loaderDeps: ({ search }) => ({
    months: search.months ?? DEFAULT_MONTH_WINDOW,
  }),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.query(meQuery)
    if (!canViewErrors(me)) notFound({ throw: true })
  },
  loader: ({ context, deps }) => {
    void context.queryClient.query(errorReports(deps.months))
  },
  component: RouteComponent,
  errorComponent: ErrorState,
  pendingComponent: Loading,
})
